import {
  faAngleLeft,
  faDisplay,
  faMobile,
  faMoon,
  faSunAlt,
  faTablet,
} from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { ToggleButton } from 'src/components/ToggleButton';
import { ToggleButtonGroup } from 'src/components/ToggleButtonGroup';
import { Typography } from 'src/components/Typography';
import { UnsavedChangesDialog } from 'src/components/UnsavedChangesDialog';
import { capitalize } from 'src/components/muiTheme/utils';

import { usePosPreviewConfig } from './PosPreviewConfigContext';

export default function PosPreviewTopBar() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'posPreview',
  });

  const { hasUnsavedChanges } = usePosPreviewConfig();
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);

  const navigate = useNavigate();

  const confirmLeave = () => {
    navigate('/pos-config', {
      replace: true,
    });
  };

  // TODO add compability to browser back button
  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true);
      return;
    }

    confirmLeave();
  };

  const {
    viewMode,
    theme,
    handleThemeChange,
    handleViewModeChange,
    posType,
    posConfig,
    handleSaveConfig,
    isUpdatingConfig,
  } = usePosPreviewConfig();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        height: 64,
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        bgcolor: 'background.paper',
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="borderless"
          startIcon={<Icon icon={faAngleLeft} />}
          color="primary"
          onClick={handleBack}
          sx={{ textTransform: 'none', minWidth: 0, p: 0 }}
        />
        <Typography variant="body2">
          {t('customize', { posType: capitalize(posType) })}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <ToggleButtonGroup
          value={viewMode}
          onChange={(_, newValue) => {
            handleViewModeChange(newValue);
          }}
          aria-label="view mode"
          exclusive
          size="small"
          fullWidth
        >
          <ToggleButton value="desktop">
            <Icon icon={faDisplay} />
          </ToggleButton>
          <ToggleButton value="tablet">
            <Icon icon={faTablet} />
          </ToggleButton>
          <ToggleButton value="mobile">
            <Icon icon={faMobile} />
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={theme}
          onChange={(_, newValue) => handleThemeChange(newValue)}
          aria-label="theme"
          exclusive
          size="small"
          fullWidth
        >
          <ToggleButton value="dark">
            <Icon icon={faMoon} />
          </ToggleButton>
          <ToggleButton value="light">
            <Icon icon={faSunAlt} />
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="bordered"
          color="primary"
          onClick={() => {
            handleSaveConfig(posConfig);
          }}
          sx={{ minWidth: 120 }}
          loading={isUpdatingConfig}
          disabled={isUpdatingConfig}
        >
          {t('saveChanges')}
        </Button>
      </Box>
      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onConfirm={confirmLeave}
        onCancel={() => setShowUnsavedChangesDialog(false)}
      />
    </Stack>
  );
}
